import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  X, 
  Users, 
  Clock,
  Zap,
  BookOpen,
  User,
  Award
} from 'lucide-react';
import { 
  collection, 
  getDocs, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useBusiness } from '../contexts/BusinessContext';
import { Loading } from '../components/ui/Loading';

interface ClassType {
  id: string;
  name: string;
  description?: string;
  color: string;
  duration: number;
  maxCapacity: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'all_levels';
  category?: string;
  isActive: boolean;
  instructorIds?: string[];
  image?: string;
  businessId: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  specialties?: string[];
  classTypeIds?: string[];
}

const difficultyLabels: Record<string, { label: string; color: string; bgColor: string }> = {
  beginner: { label: 'Principiante', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  intermediate: { label: 'Intermedio', color: 'text-yellow-700 dark:text-yellow-300', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
  advanced: { label: 'Avanzado', color: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-100 dark:bg-red-900/30' },
  all_levels: { label: 'Todos los niveles', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
};

const ClassesShowcasePage: React.FC = () => {
  const { businessId } = useBusiness();
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState<ClassType | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!businessId) {
        setLoading(false);
        return;
      }

      try {
        // Cargar tipos de clase activos
        const classTypesQuery = query(
          collection(db, 'classTypes'),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const classTypesSnapshot = await getDocs(classTypesQuery);
        const classTypesData = classTypesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ClassType[];

        // Cargar instructores
        const instructorsQuery = query(
          collection(db, 'instructors'),
          where('businessId', '==', businessId)
        );
        const instructorsSnapshot = await getDocs(instructorsQuery);
        const instructorsData = instructorsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Instructor[];

        setClassTypes(classTypesData);
        setInstructors(instructorsData);
      } catch (error) {
        console.error('Error loading class types:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [businessId]);

  // Obtener instructores que imparten una clase (basado en classTypeIds del instructor)
  const getClassInstructors = (classTypeId: string) => {
    return instructors.filter(i => i.classTypeIds?.includes(classTypeId));
  };

  // Generar gradiente basado en el color de la clase
  const getGradient = (color: string) => {
    // Si es un color hex, usarlo directamente
    if (color.startsWith('#')) {
      return `linear-gradient(135deg, ${color}dd, ${color}99)`;
    }
    // Colores predefinidos
    const gradients: Record<string, string> = {
      red: 'linear-gradient(135deg, #ef4444, #dc2626)',
      blue: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      green: 'linear-gradient(135deg, #22c55e, #16a34a)',
      yellow: 'linear-gradient(135deg, #eab308, #ca8a04)',
      purple: 'linear-gradient(135deg, #a855f7, #9333ea)',
      pink: 'linear-gradient(135deg, #ec4899, #db2777)',
      orange: 'linear-gradient(135deg, #f97316, #ea580c)',
      teal: 'linear-gradient(135deg, #14b8a6, #0d9488)',
      indigo: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    };
    return gradients[color] || 'linear-gradient(135deg, #6366f1, #4f46e5)';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-400">Cargando clases...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Nuestras Clases</title>
      </Helmet>

      <div className="min-h-screen">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-amber-400 mb-4">
            Nuestras Clases
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Descubre todas las disciplinas que ofrecemos para tu entrenamiento
          </p>
        </div>

        {/* Grid de clases */}
        {classTypes.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              No hay clases disponibles en este momento
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto px-4">
            {classTypes.map((classType) => (
              <div
                key={classType.id}
                onClick={() => setSelectedClass(classType)}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  {/* Imagen o gradiente de fondo */}
                  <div className="aspect-[3/4] overflow-hidden">
                    {classType.image ? (
                      <img
                        src={classType.image}
                        alt={classType.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: getGradient(classType.color) }}
                      >
                        <div className="text-center">
                          <Zap className="w-20 h-20 text-white/80 mx-auto mb-4" />
                          <span className="text-6xl font-bold text-white/60">
                            {classType.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Overlay con nombre */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                    <h3 className="text-2xl font-bold text-white text-center">
                      {classType.name}
                    </h3>
                    {classType.category && (
                      <p className="text-white/70 text-center text-sm mt-1">
                        {classType.category}
                      </p>
                    )}
                  </div>

                  {/* Badge de dificultad */}
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${difficultyLabels[classType.difficulty]?.bgColor} ${difficultyLabels[classType.difficulty]?.color}`}>
                      {difficultyLabels[classType.difficulty]?.label || 'Todos'}
                    </span>
                  </div>

                  {/* Duración */}
                  <div className="absolute top-4 left-4 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                    <Clock className="w-4 h-4 text-white" />
                    <span className="text-white text-sm font-medium">{classType.duration} min</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de detalle de la clase */}
        {selectedClass && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center z-50"
            onClick={() => setSelectedClass(null)}
          >
            <div 
              className="bg-gray-900 rounded-t-3xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con imagen/gradiente */}
              <div className="relative h-56 sm:h-72">
                {selectedClass.image ? (
                  <img
                    src={selectedClass.image}
                    alt={selectedClass.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: getGradient(selectedClass.color) }}
                  >
                    <Zap className="w-24 h-24 text-white/80" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
                
                {/* Botón cerrar */}
                <button
                  onClick={() => setSelectedClass(null)}
                  className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm hover:bg-black/70 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>

                {/* Nombre sobre la imagen */}
                <div className="absolute bottom-6 left-6 right-6">
                  <h2 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">
                    {selectedClass.name}
                  </h2>
                  {selectedClass.category && (
                    <p className="text-white/70 mt-1">{selectedClass.category}</p>
                  )}
                </div>
              </div>

              {/* Contenido */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-18rem)]">
                {/* Info rápida */}
                <div className="flex flex-wrap gap-3">
                  <div className={`px-4 py-2 rounded-full border ${
                    selectedClass.difficulty === 'beginner' ? 'bg-green-500/20 border-green-500/30 text-green-400' :
                    selectedClass.difficulty === 'intermediate' ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400' :
                    selectedClass.difficulty === 'advanced' ? 'bg-red-500/20 border-red-500/30 text-red-400' :
                    'bg-blue-500/20 border-blue-500/30 text-blue-400'
                  }`}>
                    <span className="font-medium">
                      {difficultyLabels[selectedClass.difficulty]?.label || 'Todos los niveles'}
                    </span>
                  </div>
                  <div className="px-4 py-2 rounded-full bg-gray-800 border border-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-gray-300 font-medium">{selectedClass.duration} min</span>
                  </div>
                  <div className="px-4 py-2 rounded-full bg-gray-800 border border-gray-700 flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-400" />
                    <span className="text-gray-300 font-medium">Máx. {selectedClass.maxCapacity}</span>
                  </div>
                </div>

                {/* Descripción */}
                {selectedClass.description && (
                  <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                    <p className="text-gray-300 leading-relaxed">
                      {selectedClass.description}
                    </p>
                  </div>
                )}

                {/* Instructores */}
                {getClassInstructors(selectedClass.id).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Award className="w-5 h-5 text-amber-400" />
                      <h3 className="text-lg font-semibold text-white">
                        Instructores que imparten esta clase
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {getClassInstructors(selectedClass.id).map((instructor) => (
                        <div
                          key={instructor.id}
                          className="flex items-center gap-4 p-4 bg-gray-800 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors"
                        >
                          {instructor.avatar ? (
                            <img
                              src={instructor.avatar}
                              alt={instructor.firstName}
                              className="w-14 h-14 rounded-full object-cover border-2 border-amber-500/50"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                              <span className="text-xl font-bold text-white">
                                {instructor.firstName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-white">
                              {instructor.firstName} {instructor.lastName}
                            </p>
                            {instructor.specialties && instructor.specialties.length > 0 && (
                              <p className="text-sm text-gray-400">
                                {instructor.specialties.slice(0, 2).join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Si no hay instructores asignados */}
                {getClassInstructors(selectedClass.id).length === 0 && (
                  <div className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                    <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-300">
                        Instructores por asignar
                      </p>
                      <p className="text-sm text-gray-500">
                        Consulta los horarios para ver quién imparte esta clase
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClassesShowcasePage;
